import {useCallback} from 'react';

import {useEnvContext} from '@web-landing/components/EnvContext/EnvContextProvider';
import {ym_log} from '@web-landing/components/SearchPicks/SearchBlock/analytics';

import {
  AnalyticsEventParams,
  SearchBarLocation,
} from '@web-landing/analytics/types/analytics';
import {AppType, SuggestionClickParams} from '@web-landing/types/searchTypes';

import {logTmrEvent} from '@web-landing/analytics/helpers/logTmrEvent';

import {emitEcommerceSelectAppEventWithCookie} from '@web-landing/analytics/emitEcommerceSelectAppEventWithCookie';
import {EVENTS} from '@web-landing/analytics/events';

const appsToAnalyticsListParam = (list: Array<AppType>) =>
  list
    .map((item) =>
      item.packageName ? `app:${item.packageName}` : `query:${item.text}`,
    )
    .join(';');

export const useSearchAnalytics = (targetLocation: SearchBarLocation) => {
  const {cookieDomain} = useEnvContext();

  const searchQueryAnalytics = useCallback((inputText: string) => {
    logTmrEvent(EVENTS.searchQuery, {query: inputText});
  }, []);

  const searchBarHistoryDeleteAnalytics = useCallback(() => {
    logTmrEvent(EVENTS.searchBarHistoryDelete);
  }, []);

  const searchBarHistoryClearAllAnalytics = useCallback(() => {
    logTmrEvent(EVENTS.searchBarHistoryClearAll);
  }, []);

  const searchBarHistoryShowAnalytics = useCallback(
    (historyList: Array<AppType>) => {
      logTmrEvent(EVENTS.searchBarHistoryShow, {
        list: appsToAnalyticsListParam(historyList),
        count: historyList.length,
        location: targetLocation,
      });
    },
    [targetLocation],
  );

  const searchBarSuggestionsShowAnalytics = useCallback(
    (inputText: string, suggestionsList: Array<AppType>) => {
      logTmrEvent(EVENTS.searchBarSuggestionsShow, {
        list: appsToAnalyticsListParam(suggestionsList),
        count: suggestionsList.length,
        query: inputText,
        location: targetLocation,
      });
    },
    [targetLocation],
  );

  const searchBarSuggestionsClickAnalytics = useCallback(
    ({item, isHistory, position, query}: SuggestionClickParams) => {
      let type: Required<AnalyticsEventParams['type_suggest']>;

      if (isHistory) {
        type = item.packageName ? 'history_app' : 'history';
      } else {
        type = item.packageName ? 'app' : 'query';
      }

      logTmrEvent(EVENTS.searchBarSuggestionsClick, {
        type_suggest: type,
        position,
        query,
        location: targetLocation,
        text: item.text,
      });

      if (item.packageName) {
        emitEcommerceSelectAppEventWithCookie({
          packageName: item.packageName,
          listName: 'Поиск / Подсказки',
          position,
          cookieDomain,
        });
      }
    },
    [targetLocation, cookieDomain],
  );

  const searchBarSuggestionsEnterAnalytics = useCallback(
    (inputText: string, suggestionsList: Array<AppType>) => {
      logTmrEvent(EVENTS.searchBarSuggestionsEnter, {
        list: appsToAnalyticsListParam(suggestionsList),
        query: inputText,
        location: targetLocation,
      });
    },
    [targetLocation],
  );

  const handleInputClickAnalytics = useCallback(() => {
    logTmrEvent(EVENTS.searchBarClick, {location: targetLocation});
    ym_log.searchBarClick();
  }, [targetLocation]);

  return {
    searchQueryAnalytics,
    searchBarHistoryDeleteAnalytics,
    searchBarHistoryClearAllAnalytics,
    searchBarHistoryShowAnalytics,
    searchBarSuggestionsShowAnalytics,
    searchBarSuggestionsClickAnalytics,
    searchBarSuggestionsEnterAnalytics,
    handleInputClickAnalytics,
  } as const;
};
